import {
  columnIndexByUuid,
  readCellLabelText,
  TransposePivotSpec,
} from 'notebook/structured-tables/TransposePivot';
import {Table} from 'notebook/tables/constants/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';

import type {ParameterStructuredTableRowMetadata} from './ParameterStructuredTableAdapter';
import {ParameterStructuredTableColumnUuid} from './ParameterStructuredTableColumnInfo';

/**
 * The `TransposePivotSpec` for procedure parameter CONFIRMATION tables. This is the ONLY place that
 * knows the parameter-confirmation domain: which columns hold the parameter name, the value to
 * confirm, and the condition-replicate identity, and how a canonical row's (parameter, PMEI) identity
 * maps onto the transposed grid (parameters -> rows, condition x replicate "PMEIs" -> columns).
 *
 * Reached only through `ParameterStructuredTableAdapter`, so its heavy parameter/condition-replicate
 * imports load lazily with the adapter rather than statically from `StructuredTableFormOrView`.
 */

const NAME_COLUMN_UUID = ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID;
const CONFIRM_COLUMN_UUID = ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID;
const CONDITION_REPLICATE_COLUMN_UUID = ParameterStructuredTableColumnUuid.CONDITION_REPLICATE_COLUMN_UUID;

/** Column key for the degenerate case with no condition-replicate (single implicit batch). */
const SINGLE_BATCH_KEY = '__single__';

/** The header label for a PMEI column, e.g. "Condition A · Replicate 1". */
function pmeiColumnLabel(conditionName: string, replicateLabel: string): string {
  const parts = [conditionName, replicateLabel].filter((part) => part !== '');
  return parts.length > 0 ? parts.join(' · ') : 'Value';
}

/**
 * Run-stable column key for a condition-replicate: `conditionName::replicateNumber`. Used instead of the
 * per-run PMEI so a column keeps its identity — and its persisted width — across worksheets running the
 * same unit operation.
 */
function conditionReplicateColumnKey(conditionName: string, replicateNumber: number | undefined): string {
  return `${conditionName}::${replicateNumber ?? ''}`;
}

export const PARAMETER_CONFIRMATION_PIVOT_SPEC: TransposePivotSpec = {
  projectedColumnUuid: CONFIRM_COLUMN_UUID,
  title: 'Confirm parameter setpoints',
  // The transposed pivot-mode grid skips crash-prone primary-sample logic.
  hasPrimarySamples: false,
  // Persist widths per unit operation (template PMDV). Every row shares one (all PSEIs have identical
  // parameters), so the first row that carries it is representative; null falls back to a per-table key.
  getColumnWidthStoreKey: (table: Table) => {
    for (const rowData of table.rowData ?? []) {
      const rowMeta = rowData?.rowMetadata as ParameterStructuredTableRowMetadata | undefined;
      if (rowMeta?.templateMethodDefinitionVersionId != null) {
        return rowMeta.templateMethodDefinitionVersionId;
      }
    }
    return null;
  },
  createRowClassifier: (table: Table) => {
    const colIndexByUuid = columnIndexByUuid(table);
    const nameColIndex = colIndexByUuid.get(NAME_COLUMN_UUID);
    const condRepColIndex = colIndexByUuid.get(CONDITION_REPLICATE_COLUMN_UUID);

    return (rowIndex: number) => {
      const rowMeta = table.rowData?.[rowIndex]?.rowMetadata as
        | ParameterStructuredTableRowMetadata
        | undefined;

      // Row axis: one grid row per parameter.
      const rowKey = rowMeta?.parameterId ?? `row-${rowIndex}`;
      const rowLabel = nameColIndex == null ? '' : readCellLabelText(table.table[rowIndex]?.[nameColIndex]);

      // Column axis: one grid column per condition x replicate. A row's PMEI signals whether it is a
      // condition-replicate row at all; the column is then keyed run-stably (not by the per-run PMEI).
      const pmei = rowMeta?.methodExecutionInstanceId;
      if (pmei == null || condRepColIndex == null) {
        return {rowKey, rowLabel, columnKey: SINGLE_BATCH_KEY, columnLabel: pmeiColumnLabel('', '')};
      }

      const typedValue = table.table[rowIndex]?.[condRepColIndex]?.typedValue;
      // A PMEI row carries exactly one condition-replicate; guard the collection so a malformed or
      // partially hydrated (empty) list falls back to the single-batch column rather than throwing.
      const conditionReplicate =
        typedValue?.type === BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES &&
        typedValue.conditionReplicates.length > 0
          ? typedValue.conditionReplicates[0]
          : undefined;
      const conditionName = conditionReplicate?.conditionName ?? '';
      const replicateLabel =
        conditionReplicate?.replicateNumber != null ? `Replicate ${conditionReplicate.replicateNumber}` : '';

      return {
        rowKey,
        rowLabel,
        columnKey: conditionReplicateColumnKey(conditionName, conditionReplicate?.replicateNumber),
        columnLabel: pmeiColumnLabel(conditionName, replicateLabel),
      };
    };
  },
};
